import { useEffect, useState } from "react"
import { useLocation } from "react-router"
import { useAuthApi } from "@/shared/api/authApi/useAuthApi"
import { getPublicConfig } from "@/shared/config/config"
import { captureMicrofrontendError } from "./captureMicrofrontendError"
import { mfeConfig } from "./helpers"
import type { MfeManifest, MfeProps, MicroFrontendContentProps } from "./interfaces"

export function MicroFrontendManifest(mfe: MicroFrontendContentProps) {
  const config = mfeConfig(mfe)

  const [loaded, setLoaded] = useState<boolean>(false)
  const [error, setError] = useState<string>("")
  const location = useLocation()
  const authApi = useAuthApi()

  // biome-ignore lint/correctness/useExhaustiveDependencies: Preserving the same deps list as the original code
  useEffect(() => {
    const props: MfeProps = {
      mfeConfig: {
        containerId: config.container,
        environment: getPublicConfig().environment,
        urlPath: location.pathname,
        customerId: mfe.customerId
      },
      api: {
        user: {
          token: authApi.getToken
        }
      }
    }

    if (config.loaded) {
      // If script has been loaded to the HTML, just re-render the frontend
      config.renderMfe(props)
      setLoaded(true)
    } else {
      // load mfe
      fetch(config.manifest)
        .then((response) => response.json())
        .then((data: Record<string, MfeManifest>) => {
          const components = Object.values(data).filter((item) => item.isEntry)
          if (components.length > 0) {
            const targetFirstComponent = components[0]

            config.appendDocumentMfe({
              src: targetFirstComponent.file,
              css: targetFirstComponent.css,
              props
            })
            setLoaded(true)
          } else {
            throw Error("Not single mfe found")
          }
        })
        .catch((error: unknown) => {
          const errorMessage = `Error occurred loading micro-frontend from ${config.manifest}`
          setError(errorMessage)

          captureMicrofrontendError(error, {
            mfeName: mfe.webComponentName,
            customerId: mfe.customerId,
            errorType: "mfe_load_error",
            context: {
              manifest_url: config.manifest,
              container_id: config.container
            }
          })
        })
    }
    return config.removeMfe
  }, [mfe.customerId])

  if (error) {
    return <div style={{ padding: 10, color: "red" }}>{error}</div>
  }

  if (!loaded) {
    return <div>Loading</div>
  }

  return <div id={config.container} data-testid={config.container} />
}
